/* ============================================================
 * Donate sky — bootstrap + quality + lazy load + render
 *
 * Public API on window.DonateSky:
 *   .init()                 — must be called once with the sky in DOM
 *   .applyQuality(q)        — 'auto'|'low'|'high'  (auto runs detectQuality)
 *   .detectQuality()        — synchronous heuristic returning 'low'|'high'
 *   .reload()               — refetch /api/wishes + /api/sky-state
 *   .submitWish(name,msg)   — POST /api/wishes; resolves with response
 *   .recordCtaClick()       — for telemetry hook on CTA buttons
 *
 * The blade caller wires DonateSky.submitWish() into the wish form.
 * Everything else self-organises after .init().
 * ============================================================ */
(function (window, document) {
    'use strict';

    var DonateSky = {};

    // ───── i18n ─────
    // All user-visible legend / body-name / star-class / quality strings come
    // from window.__SKY_I18N (emitted by the <x-cosmic-sky> blade component
    // from resources/lang/<locale>/cosmic.php → 'sky'). RU on /ru/donate, the
    // visitor's language on the intl shard. English fallbacks below only fire
    // if the object is missing (e.g. cached HTML during a deploy) — they keep
    // the shipped JS free of any non-English text.
    var I18 = (window.__SKY_I18N || {});

    // ───── State ─────
    var skyEl, skySection, skyEmpty, totalEl, starsLayer, canvasLayer, ctx,
        farLayer, galaxyLayer, midLayer, constLayer, planetLayer,
        wishesData = [],
        slotsCache = null,
        canvasFreeStars = [], // overflow stars when wishes > 200
        useCanvas = false,
        anonText = 'Anonymous',
        rafId = null,
        loadedOnce = false,
        brightRotateTimer = null,
        loadedModules = {};

    // ───── Module loader ─────
    // Lazily injects /js/donate/<name>.js once. Version stamp comes from the
    // blade (window.DonateSkyVersion) so cached older versions get blown away
    // when we ship a new chunk.
    DonateSky.loadModule = function (name) {
        if (loadedModules[name]) return loadedModules[name];
        loadedModules[name] = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.async = true;
            var ver = window.DonateSkyVersion || Date.now();
            s.src = '/js/donate/' + name + '.js?v=' + ver;
            s.onload  = function () { resolve(window['Donate' + capitalise(name.replace(/-/g, ''))]); };
            s.onerror = function () { delete loadedModules[name]; reject(new Error('failed to load ' + name)); };
            document.head.appendChild(s);
        });
        return loadedModules[name];
    };
    function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

    var FREE_STAR_DOM_LIMIT = 200; // above this, draw on Canvas instead

    // Twinkle variants (CSS animation names) — assigned deterministically per id
    var TWINKLE = [
        { name: 'dn-twinkle',         dur: '3s'   },
        { name: 'dn-twinkle-slow',    dur: '5.5s' },
        { name: 'dn-twinkle-fast',    dur: '2.2s' },
        { name: 'dn-twinkle-sparkle', dur: '7s'   },
    ];

    // Real-star spectral classes (Morgan-Keenan O B A F G K M, simplified)
    // — applied to constellation slots (filled with wishes) so each visitor's
    //   star looks like a different real star.
    // pri = primary colour (the hot core), sec = halo tint.
    // Labels (the `label` field) are pulled from I18.star_classes by spectral
    // class so they render in the page language; English here is only a fallback.
    var SC = I18.star_classes || {};
    var SPECTRAL = [
        { pri: '#aabfff', sec: '#7da7ff', cls: 'O', label: SC.O || 'blue O' }, // hottest
        { pri: '#cad7ff', sec: '#9eb5ff', cls: 'B', label: SC.B || 'blue-white B' },
        { pri: '#f8f7ff', sec: '#dde2f5', cls: 'A', label: SC.A || 'white A' },
        { pri: '#fff4ea', sec: '#fde2c2', cls: 'F', label: SC.F || 'yellow-white F' },
        { pri: '#fff2a1', sec: '#facc15', cls: 'G', label: SC.G || 'yellow G (like the Sun)' },
        { pri: '#ffd2a1', sec: '#fb923c', cls: 'K', label: SC.K || 'orange K' },
        { pri: '#ffcc6f', sec: '#ef4444', cls: 'M', label: SC.M || 'red M' }, // coolest
    ];

    // 30 star "types" — varying size + spectral colour. Stars are tiny
    // points of light to the naked eye in real life — even Sirius (the
    // brightest) doesn't show as a disc. We squeeze the size down to
    // 3-6 px so they read as points rather than puffy badges, but stay
    // visible at any zoom level (and big enough for hover-able tooltips).
    var STAR_TYPES = (function () {
        var out = [];
        for (var i = 0; i < 30; i++) {
            var sp = SPECTRAL[i % SPECTRAL.length];
            var size = 3 + (i % 4);                  // 3..6 px
            out.push({
                size: size + 'px',
                pri: sp.pri, sec: sp.sec, sclass: sp.cls,
            });
        }
        return out;
    })();
    function pickType(id) { return STAR_TYPES[Math.abs(id | 0) % STAR_TYPES.length]; }

    // ───── Quality tiers ─────
    // Three options for the user-facing dropdown:
    //   'auto' — let detectQuality() pick low/high from device signals
    //            (prefers-reduced-motion / hardware concurrency / device memory /
    //            touch + small screen / save-data / 2-3g connection). Run lazily
    //            only when the sky becomes near-visible, so the cost is paid
    //            once per session and never on devices that don't even scroll
    //            down to the wall.
    //   'low'  — forced reduced effects (galaxy, comets, large nebulas hidden,
    //            star pulse animation off). Respects prefers-reduced-motion.
    //   'high' — forced full effects regardless of OS reduce-motion.
    //
    // Internal effect classes on .dn-sky are still binary: dn-q-low / dn-q-high.
    // 'auto' is a *user preference*, persisted as 'auto' in localStorage; the
    // resolved tier (low/high) is applied as the CSS class.
    function applyQuality(q) {
        if (!skyEl) return;
        var resolved = (q === 'auto') ? detectQuality() : q;
        if (resolved !== 'high' && resolved !== 'low') resolved = 'low';
        skyEl.classList.remove('dn-q-low', 'dn-q-high');
        skyEl.classList.add('dn-q-' + resolved);
        // Persist the user's preference, not the resolved tier — so on next
        // visit "Auto" stays "Auto" and a faster device gets High again.
        try {
            localStorage.setItem('dn_sky_q', (q === 'auto' || q === 'low' || q === 'high') ? q : 'auto');
        } catch (_) {}
    }
    DonateSky.applyQuality = applyQuality;

    // Cheap, synchronous heuristic — runs in microseconds, no DOM probing.
    // Returns 'low' or 'high'. Single-flag policy: ANY weak signal flips us
    // to Low, because the cost of misjudging upward (jank) is much worse
    // than misjudging downward (a slightly less ornate sky).
    function detectQuality() {
        var nav = navigator || {};

        // (1) Explicit user/OS signal — battery saver, accessibility, etc.
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return 'low';
        }

        // (2) Hardware: cores < 4 or RAM < 4 GB → almost always a budget phone.
        // hardwareConcurrency may be 0 or undefined on locked-down browsers; only
        // act when we have a positive number that's clearly under the threshold.
        if (typeof nav.hardwareConcurrency === 'number' &&
            nav.hardwareConcurrency > 0 && nav.hardwareConcurrency < 4) {
            return 'low';
        }
        if (typeof nav.deviceMemory === 'number' &&
            nav.deviceMemory > 0 && nav.deviceMemory < 4) {
            return 'low';
        }

        // (3) Touch + small screen — phones, especially the long tail of
        // mid-range Android. Tablets (coarse pointer + ≥700px) escape this
        // branch and get High if their CPU is fast enough.
        var coarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
        var smallScreen   = (window.innerWidth || (window.screen && window.screen.width) || 0) < 700;
        if (coarsePointer && smallScreen) return 'low';

        // (4) Network signal: when save-data is on or the link is 2g/slow-2g,
        // the user is on a constrained connection and likely a constrained
        // device too. RU mobile audiences with throttled connections fit here.
        var conn = nav.connection || nav.mozConnection || nav.webkitConnection;
        if (conn) {
            if (conn.saveData === true) return 'low';
            if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return 'low';
        }

        return 'high';
    }
    DonateSky.detectQuality = detectQuality;

    // ───── Pseudorandom but deterministic position derived from id ─────
    function posFromId(id) {
        var x = ((id * 9301 + 49297) % 233280) / 233280;
        var y = ((id * 4691 + 17389) % 233280) / 233280;
        return { x: 5 + x * 90, y: 8 + y * 80 };
    }

    // 5-point inflated star ★ — dual-layer (blurred glow + sharp shape).
    // Path is built procedurally from 5 outer cusps, with QUADRATIC
    // control points pulled toward the centre to give a "puffy / inflated"
    // look (vs. straight-line classic 5-star). Tiny by default (2-6 px).
    var STAR_PATH = (function () {
        // Outer points at -90°, -90°+72°, etc. (top, then around clockwise)
        // viewBox is 0..24, centre 12,12, outer radius 11
        var R = 11, IR = 4.6;          // outer / inner-control radius
        var pts = [];
        for (var i = 0; i < 5; i++) {
            var aOut = -Math.PI / 2 + i * (2 * Math.PI / 5);
            var aIn  = aOut + Math.PI / 5;
            pts.push({
                ox: 12 + R * Math.cos(aOut), oy: 12 + R * Math.sin(aOut),
                cx: 12 + IR * Math.cos(aIn), cy: 12 + IR * Math.sin(aIn),
            });
        }
        // Move to first outer; then for each i: Q control -> next outer.
        var d = 'M ' + pts[0].ox.toFixed(2) + ' ' + pts[0].oy.toFixed(2);
        for (var j = 0; j < 5; j++) {
            var p = pts[j], next = pts[(j + 1) % 5];
            d += ' Q ' + p.cx.toFixed(2) + ' ' + p.cy.toFixed(2)
              + ' ' + next.ox.toFixed(2) + ' ' + next.oy.toFixed(2);
        }
        d += ' Z';
        return d;
    })();
    function sparkleStarSVG() {
        return '<svg class="dn-star-svg" viewBox="0 0 24 24" aria-hidden="true">' +
                 '<path class="dn-star-glow"  d="' + STAR_PATH + '"/>' +
                 '<path class="dn-star-shape" d="' + STAR_PATH + '"/>' +
               '</svg>';
    }
    // Public — sky-detail.js or constellation slot upgrade can use the same path
    DonateSky.STAR_PATH = STAR_PATH;

    // ───── DOM star renderer (used when free-stars are few) ─────
    function renderDomStar(w, opts) {
        opts = opts || {};
        var id = w.id || Math.floor(Math.random() * 1e9);
        var p = posFromId(id);
        var t = pickType(id);
        var star = document.createElement('div');
        // Atmospheric twinkle hint by altitude — stars near horizon (high y%)
        // are seen through more atmosphere, so they twinkle harder. Stars at
        // zenith (low y%) are nearly steady. CSS reads the modifier class.
        var altClass = '';
        if      (p.y > 65) altClass = ' is-low-alt';
        else if (p.y < 25) altClass = ' is-high-alt';
        // Pro stars (authored by a paid account) get a distinct violet look —
        // donor gold > Pro violet > regular spectral. is_donor wins if both.
        var isPro = !!w.is_pro && !w.is_donor;
        star.className = 'dn-star'
            + (w.is_donor ? ' is-donor' : '')
            + (isPro ? ' is-pro' : '')
            + (opts.flying ? ' flying' : '') + altClass;
        star.style.setProperty('--x', p.x);
        star.style.setProperty('--y', p.y);
        // Sparkle-star CSS vars — size + colour (consumed by sky.css). Inline
        // wins over the stylesheet, so Pro stars get their violet here directly
        // (mirrors how donor stars are coloured). Size stays the spectral size.
        star.style.setProperty('--size', t.size);
        star.style.setProperty('--c',    isPro ? '#c4b5fd' : t.pri);
        star.style.setProperty('--c2',   isPro ? '#7c3aed' : t.sec);
        star.style.setProperty('--delay', ((id % 30) / 10) + 's');
        star.style.setProperty('--drift-x', (((id * 7) % 5) - 2) + 'px');
        star.style.setProperty('--drift-y', (((id * 13) % 5) - 2) + 'px');
        var v = TWINKLE[id % TWINKLE.length];
        star.style.setProperty('--twinkle-anim', v.name);
        star.style.setProperty('--twinkle-dur',  v.dur);
        // 4-point sparkle SVG. Tooltip via body-level portal (so it can
        // overflow the sky frame freely).
        star.innerHTML = sparkleStarSVG();
        star.dataset.name = w.name || anonText;
        star.dataset.msg  = w.msg || w.message || '';
        if (w.id != null) star.dataset.wishId = String(w.id);
        if (w.is_donor && w.amount) star.dataset.amount = Math.round(w.amount);
        if (isPro) star.dataset.pro = '1';
        if (w.profile_url) star.dataset.profileUrl = w.profile_url;
        var tipData = function () {
            return { name: star.dataset.name, msg: star.dataset.msg, amount: star.dataset.amount, isPro: isPro, profileUrl: star.dataset.profileUrl };
        };
        star.addEventListener('mouseenter', function () { showSlotTip(star, tipData()); });
        star.addEventListener('mouseleave', hideSlotTip);
        star.addEventListener('click', function (e) {
            e.stopPropagation();
            document.querySelectorAll('.dn-star.active').forEach(function (s) { if (s !== star) s.classList.remove('active'); });
            star.classList.toggle('active');
            showSlotTip(star, tipData());
        });
        return star;
    }

    // ───── Canvas star renderer (used when free-stars are many) ─────
    function ensureCanvas() {
        if (canvasLayer) return canvasLayer;
        canvasLayer = document.createElement('canvas');
        canvasLayer.className = 'dn-stars-canvas';
        skyEl.appendChild(canvasLayer);
        ctx = canvasLayer.getContext('2d');
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas, { passive: true });
        return canvasLayer;
    }
    function resizeCanvas() {
        if (!canvasLayer) return;
        var dpr = window.devicePixelRatio || 1;
        var rect = skyEl.getBoundingClientRect();
        canvasLayer.width = Math.round(rect.width * dpr);
        canvasLayer.height = Math.round(rect.height * dpr);
        if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function pushCanvasStar(w) {
        var id = w.id || Math.floor(Math.random() * 1e9);
        var p = posFromId(id);
        // Canvas path is overflow-only (many stars) — no per-star tooltip/link,
        // but Pro stars still keep their violet tint so the perk reads at a glance.
        var isPro = !!w.is_pro && !w.is_donor;
        canvasFreeStars.push({
            id: id,
            x: p.x, y: p.y,
            size: w.is_donor ? 4 : (isPro ? 3.2 : 2.5),
            color: w.is_donor ? '#fde047' : (isPro ? '#c4b5fd' : '#e0e7ff'),
            phase: (id % 30) / 5,    // twinkle offset
            speed: 0.6 + ((id % 17) / 30), // 0.6..1.2
            name: w.name || anonText,
            msg:  w.msg || w.message || '',
        });
    }
    function tickCanvas(now) {
        if (!ctx || !canvasLayer) return;
        var w = canvasLayer.width  / (window.devicePixelRatio || 1);
        var h = canvasLayer.height / (window.devicePixelRatio || 1);
        ctx.clearRect(0, 0, w, h);
        var t = now / 1000;
        for (var i = 0; i < canvasFreeStars.length; i++) {
            var s = canvasFreeStars[i];
            var a = 0.55 + 0.45 * Math.sin(s.phase + t * s.speed);
            ctx.globalAlpha = a;
            ctx.fillStyle = s.color;
            ctx.shadowColor = s.color;
            ctx.shadowBlur = 8;
            ctx.beginPath();
            ctx.arc(s.x * w / 100, s.y * h / 100, s.size, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        rafId = requestAnimationFrame(tickCanvas);
    }
    function startCanvasLoop() {
        if (rafId) return;
        rafId = requestAnimationFrame(tickCanvas);
    }
    function stopCanvasLoop() {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    // ───── Constellation slot fill ─────
    function fillSlot(slotEl, wish, opts) {
        opts = opts || {};
        slotEl.classList.add('is-filled');
        if (wish.is_donor) slotEl.classList.add('is-donor');
        var isPro = !!wish.is_pro && !wish.is_donor;
        if (isPro) slotEl.classList.add('is-pro');
        if (wish.id != null) slotEl.dataset.wishId = String(wish.id);
        var id = wish.id || 0;
        var t  = pickType(id);
        // Filled slot inherits a real spectral colour (varies per wish id) —
        // overrides the constellation's nominal star colour for visual variety.
        // Donor gold > Pro violet > regular spectral.
        var color = wish.is_donor ? '#fde047' : (isPro ? '#c4b5fd' : t.pri);
        slotEl.style.setProperty('--star-color', color);
        var rhythms = ['3.5s', '5s', '6.5s', '8s'];
        slotEl.style.setProperty('--pulse-dur',   rhythms[id % rhythms.length]);
        slotEl.style.setProperty('--pulse-delay', ((id % 30) / 10) + 's');

        slotEl.addEventListener('mouseenter', function () { showSlotTip(slotEl, wish); });
        slotEl.addEventListener('mouseleave', hideSlotTip);
        slotEl.addEventListener('click', function (e) { e.stopPropagation(); showSlotTip(slotEl, wish, true); });

        if (opts.flying) {
            slotEl.style.animation = 'dn-slot-fly 1.6s cubic-bezier(.2,.7,.2,1)';
            setTimeout(function () { slotEl.style.animation = ''; }, 1700);
        }

        var parentC = slotEl.closest('.dn-constellation');
        if (parentC) parentC.classList.add('has-fills');
    }

    // Tooltip is appended to <body> rather than the sky element —
    // this lets it escape any clipping/overflow without forcing
    // overflow:visible on the sky frame.
    var tipEl = null;
    var tipHasLink = false;       // current tip carries a profile link (Pro star)
    var tipHideTimer = null;
    function showSlotTip(targetEl, wish) {
        if (!tipEl) {
            tipEl = document.createElement('div');
            tipEl.className = 'dn-slot-tip dn-slot-tip-portal';
            tipEl.innerHTML = '<span class="dn-slot-tip-pro" hidden>Pro</span>'
                + '<span class="dn-slot-tip-name"></span>'
                + '<span class="dn-slot-tip-msg"></span>'
                + '<span class="dn-slot-tip-amt"></span>';
            // Keep the tip open while the pointer is on it — but only matters
            // for Pro stars whose tip holds a clickable profile link. Regular
            // stars still vanish immediately (tipHasLink === false).
            tipEl.addEventListener('mouseenter', function () { if (tipHideTimer) { clearTimeout(tipHideTimer); tipHideTimer = null; } });
            tipEl.addEventListener('mouseleave', function () { tipEl.classList.remove('show'); });
            document.body.appendChild(tipEl);
        }
        if (tipHideTimer) { clearTimeout(tipHideTimer); tipHideTimer = null; }
        var amount = wish.amount != null ? Number(wish.amount) : null;
        var hasAmount = amount && !isNaN(amount);
        var rawName = wish.name && String(wish.name).trim();
        var profileUrl = wish.profileUrl || wish.profile_url || null;
        var isProWish  = !!(wish.isPro || wish.is_pro);
        tipHasLink = !!profileUrl;
        // Only link-bearing (Pro) tips become interactive — base tips keep
        // pointer-events:none so they never swallow clicks on stars behind them.
        tipEl.classList.toggle('has-link', tipHasLink);
        // Anonymous donor: name was either omitted or matches the localised
        // anonymous label. We don't show the anonymous label + sum — just the
        // sum on its own line to keep the star clean and gift-like.
        var isAnonDonor = hasAmount && (!rawName || rawName === anonText);
        var proEl  = tipEl.querySelector('.dn-slot-tip-pro');
        var nameEl = tipEl.querySelector('.dn-slot-tip-name');
        var msgEl  = tipEl.querySelector('.dn-slot-tip-msg');
        var amtEl  = tipEl.querySelector('.dn-slot-tip-amt');
        proEl.hidden = !isProWish;
        if (isAnonDonor) {
            nameEl.style.display = 'none';
            msgEl.style.display  = 'none';
        } else {
            nameEl.style.display = '';
            msgEl.style.display  = '';
            // Pro star → name is a link to the author's public profile.
            if (profileUrl) {
                nameEl.innerHTML = '';
                var a = document.createElement('a');
                a.className = 'dn-slot-tip-link';
                a.target = '_blank';
                a.rel = 'noopener';
                a.href = profileUrl;
                a.textContent = rawName || anonText;
                nameEl.appendChild(a);
            } else {
                nameEl.textContent = rawName || anonText;
            }
            msgEl.textContent  = wish.msg || wish.message || '';
            // Hide message line when there's nothing to say
            if (!msgEl.textContent) msgEl.style.display = 'none';
        }
        if (hasAmount) {
            amtEl.textContent = Math.round(amount) + ' ₽';
            amtEl.style.display = 'inline-block';
        } else {
            amtEl.style.display = 'none';
        }
        var rect = targetEl.getBoundingClientRect();
        // Position at viewport coords + scroll, keeping clear of viewport edges.
        var pageX = rect.left + rect.width / 2 + window.pageXOffset;
        var pageY = rect.top + window.pageYOffset - 12;
        tipEl.style.left = pageX + 'px';
        tipEl.style.top  = pageY + 'px';
        tipEl.classList.add('show');
        // After it's visible, nudge horizontally if it would clip the viewport
        requestAnimationFrame(function () {
            var box = tipEl.getBoundingClientRect();
            var pad = 8;
            if (box.left < pad)            tipEl.style.left = (pageX + (pad - box.left)) + 'px';
            if (box.right > window.innerWidth - pad) {
                tipEl.style.left = (pageX - (box.right - (window.innerWidth - pad))) + 'px';
            }
        });
    }
    function hideSlotTip() {
        if (!tipEl) return;
        // Regular stars hide immediately on mouseleave — the user expects the
        // label gone the moment the cursor leaves the star. Pro stars carry a
        // clickable profile link, so give a short grace window for the pointer
        // to travel from star → tip (the tip's own mouseenter cancels the timer).
        if (tipHasLink) {
            if (tipHideTimer) clearTimeout(tipHideTimer);
            tipHideTimer = setTimeout(function () { tipEl.classList.remove('show'); tipHideTimer = null; }, 220);
        } else {
            tipEl.classList.remove('show');
        }
    }

    // ───── Slot helpers ─────
    function getSlots() {
        if (slotsCache) return slotsCache;
        var slots = Array.prototype.slice.call(document.querySelectorAll('.dn-c-slot'));
        slots.sort(function (a, b) { return parseInt(a.dataset.slot, 10) - parseInt(b.dataset.slot, 10); });
        slotsCache = slots;
        return slots;
    }
    function nextEmptySlot() {
        var slots = getSlots();
        for (var i = 0; i < slots.length; i++) {
            if (!slots[i].classList.contains('is-filled')) return slots[i];
        }
        return null;
    }

    // ───── Public: place a fresh wish ─────
    DonateSky.placeNewWish = function (wish, opts) {
        opts = opts || {};
        var slot = nextEmptySlot();
        var result;
        if (slot) {
            fillSlot(slot, wish, opts);
            result = { kind: 'slot', node: slot };
        } else if (canvasFreeStars.length + getDomFreeStarCount() >= FREE_STAR_DOM_LIMIT && !useCanvas) {
            switchToCanvas();
            pushCanvasStar(wish);
            result = { kind: 'canvas' };
        } else if (useCanvas) {
            pushCanvasStar(wish);
            result = { kind: 'canvas' };
        } else {
            var star = renderDomStar(wish, opts);
            starsLayer.appendChild(star);
            if (opts.flying) setTimeout(function () { star.classList.remove('flying'); }, 1700);
            result = { kind: 'dom', node: star };
        }

        // Celebrate paid wishes: meteor flash + aurora burst + Polaris reveal.
        // Pro stars get a lighter reward (a shorter aurora glow) — a real perk,
        // but the full meteor+Polaris narrative stays reserved for paid donors.
        // Regular free wishes get the slot animation alone.
        if (wish && wish.is_donor) {
            try { if (window.DonateSkyMeteors) window.DonateSkyMeteors.flash(); } catch (_) {}
            try { if (window.DonateSkyAurora)  window.DonateSkyAurora.flash(9000); } catch (_) {}
            togglePolaris(true);
        } else if (wish && wish.is_pro) {
            try { if (window.DonateSkyAurora)  window.DonateSkyAurora.flash(5000); } catch (_) {}
        }
        return result;
    };

    function getDomFreeStarCount() {
        return starsLayer ? starsLayer.querySelectorAll('.dn-star').length : 0;
    }

    function switchToCanvas() {
        // Convert all existing DOM stars to canvas, then keep adding to canvas
        if (!starsLayer) return;
        var existing = Array.prototype.slice.call(starsLayer.querySelectorAll('.dn-star'));
        if (existing.length === 0 && canvasFreeStars.length === 0) {
            useCanvas = true;
            ensureCanvas();
            startCanvasLoop();
            return;
        }
        ensureCanvas();
        existing.forEach(function (el) {
            // Reverse-extract data we stored
            var x = parseFloat(el.style.getPropertyValue('--x')) || 50;
            var y = parseFloat(el.style.getPropertyValue('--y')) || 50;
            var name = el.querySelector('.dn-star-tip-name')?.textContent || anonText;
            var msg  = el.querySelector('.dn-star-tip-msg')?.textContent || '';
            var isDonor = el.classList.contains('is-donor');
            canvasFreeStars.push({
                id: Math.floor(Math.random() * 1e9),
                x: x, y: y,
                size: isDonor ? 4 : 2.5,
                color: isDonor ? '#fde047' : '#e0e7ff',
                phase: Math.random() * 6,
                speed: 0.6 + Math.random() * 0.6,
                name: name, msg: msg,
            });
            el.remove();
        });
        useCanvas = true;
        startCanvasLoop();
    }

    // ───── Load wishes + sky-state ─────
    function loadWishes() {
        // Skip entirely if there's no starsLayer (cosmos mode) — wishes
        // are a donate-flow concept, no point hitting /api/wishes from
        // the standalone sky tool.
        if (!starsLayer) return Promise.resolve();
        return fetch('/api/wishes', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
            .then(function (data) {
                var list = (data.wishes || []).slice().reverse(); // ASC order — oldest first
                wishesData = list;
                if (!list.length) {
                    if (skyEmpty) skyEmpty.hidden = false;
                    return;
                }
                if (skyEmpty) skyEmpty.hidden = true;

                var slots = getSlots();
                var fragment = document.createDocumentFragment();
                var domStarsToAdd = [];

                list.forEach(function (w, i) {
                    if (i < slots.length) {
                        fillSlot(slots[i], w);
                    } else {
                        domStarsToAdd.push(w);
                    }
                });

                // Decide DOM vs Canvas for overflow
                if (domStarsToAdd.length > FREE_STAR_DOM_LIMIT) {
                    ensureCanvas();
                    useCanvas = true;
                    domStarsToAdd.forEach(pushCanvasStar);
                    startCanvasLoop();
                } else {
                    domStarsToAdd.forEach(function (w) { fragment.appendChild(renderDomStar(w)); });
                    if (fragment.childNodes.length) starsLayer.appendChild(fragment);
                }

                if (totalEl) {
                    totalEl.innerHTML = (I18.stars_in_sky || 'Stars in the sky:') +
                        ' <strong>' + list.length + '</strong>';
                    totalEl.hidden = false;
                }

                // Polaris appears only after at least one paid wish has lit up.
                // Cultural anchor + the donor's contribution literally "puts a
                // star on the map" — narrative reward for paid support.
                var anyDonor = list.some(function (w) { return w.is_donor; });
                togglePolaris(anyDonor);
            })
            .catch(function () { if (skyEmpty) skyEmpty.hidden = false; });
    }

    function togglePolaris(show) {
        var p = document.getElementById('dn-polaris');
        if (!p) return;
        if (show) {
            p.hidden = false;
            // requestAnimationFrame so the transition kicks in (without it the
            // element would jump straight to opacity:1 because we set both
            // `hidden=false` and `is-visible` in the same frame).
            requestAnimationFrame(function () {
                requestAnimationFrame(function () { p.classList.add('is-visible'); });
            });
        } else {
            p.classList.remove('is-visible');
        }
    }

    // Body display names for the legend below the sky. Pulled from
    // I18.bodies (page language); the English map is only a fallback.
    var BODY_NAMES = I18.bodies || {
        sun: 'Sun', moon: 'Moon',
        mercury: 'Mercury', venus: 'Venus',
        mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn',
        uranus: 'Uranus', neptune: 'Neptune',
    };

    function loadSkyState() {
        return fetch('/api/sky-state', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (data) {
                if (!data || !data.bodies) return;
                var visible = [], belowHorizon = [];
                Object.keys(data.bodies).forEach(function (name) {
                    var b = data.bodies[name];
                    var el = document.querySelector('.dn-planet-' + name);
                    if (!el) return;
                    if (!b.visible) {
                        el.classList.add('is-below-horizon');
                        belowHorizon.push(name);
                        return;
                    }
                    el.classList.remove('is-below-horizon');
                    el.style.opacity = '';
                    el.style.filter  = '';
                    el.style.left = b.x + '%';
                    el.style.top  = b.y + '%';

                    // Apparent size: physical angular_arcsec mapped to a visible
                    // px range (luminaries get the full disc, planets are exaggerated
                    // for visibility but ordered by their real angular size).
                    var px = sizeForBody(name, b.angular_arcsec || 0);
                    el.style.width  = px + 'px';
                    el.style.height = px + 'px';

                    // Illumination direction: sun_dx/sun_dy is a unit vector
                    // FROM the body TOWARD the Sun in screen-percent space. We
                    // map it onto the planet's SVG (0..100 viewBox) so the
                    // radialGradient highlight tracks the Sun. CSS y is down,
                    // SVG cy is also down — same sign, no flip needed.
                    if (typeof b.sun_dx === 'number' && typeof b.sun_dy === 'number') {
                        var cx = (50 + b.sun_dx * 32).toFixed(1);
                        var cy = (50 + b.sun_dy * 32).toFixed(1);
                        el.style.setProperty('--lit-cx', cx + '%');
                        el.style.setProperty('--lit-cy', cy + '%');
                    } else {
                        el.style.removeProperty('--lit-cx');
                        el.style.removeProperty('--lit-cy');
                    }

                    visible.push(name);
                });

                // Phase-bearing bodies — render a real terminator separating
                // lit/dark hemispheres. The dark side is a path of half-circle
                // + half-ellipse with rx scaled by illumination fraction.
                // sun_dx sign picks which half (L/R) is dark.
                //
                // Every body except the Sun gets the treatment now. Visibility
                // is gated by phase: the Moon always shows its terminator
                // (phases are its main visual feature); Venus/Mercury show
                // when illum < 0.7 (the rest of the time the planet looks
                // full); outer planets (Mars/Jupiter/Saturn/Uranus/Neptune)
                // only show when illum < 0.95 — for them this is a rare
                // event around opposition, and the dark sliver is real
                // (Mars: up to 16 % at max phase angle; Jupiter et al.:
                // less than 1 % normally).
                var PHASE_BODIES = ['moon', 'venus', 'mercury', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'];
                var R_BY_BODY = { saturn: 28 };   // Saturn's body is r=28; others r=46
                PHASE_BODIES.forEach(function (name) {
                    var b = data.bodies[name];
                    if (!b || typeof b.phase_illum !== 'number') return;
                    var selector = '.dn-planet-' + name +
                                   (name === 'moon' ? ' .dn-moon-terminator' : ' .dn-phase-terminator');
                    var shape = document.querySelector(selector);
                    if (!shape) return;
                    var k = b.phase_illum;
                    var R = R_BY_BODY[name] || 46;
                    var rxSigned = R * (2 * k - 1);
                    var sx = (typeof b.sun_dx === 'number') ? b.sun_dx : 1;
                    var side = sx >= 0 ? 1 : -1;
                    shape.setAttribute('d', buildMoonTerminatorPath(R, rxSigned, side));
                    var visible;
                    if      (name === 'moon')                       visible = true;       // always show phase
                    else if (name === 'venus' || name === 'mercury') visible = k < 0.7;   // inner planets crescent
                    else                                             visible = k < 0.95;  // outer planets near opposition
                    shape.setAttribute('opacity', visible ? '0.85' : '0');
                });
                // Saturn ring tilt — animate the SVG ring group based on
                // current real angle (varies -26.73°..+26.73° over 29 years).
                if (typeof data.saturn_ring_tilt === 'number') {
                    var saturnEl = document.querySelector('.dn-planet-saturn');
                    if (saturnEl) {
                        saturnEl.style.setProperty('--saturn-tilt', data.saturn_ring_tilt + 'deg');
                    }
                }

                // Expose Sun's current screen position to sub-modules
                // (sky-comets uses it to direct tails AWAY from the Sun —
                // the actual physics: solar wind pushes dust/gas anti-Sun).
                if (data.bodies.sun && data.bodies.sun.visible) {
                    DonateSky.sunScreenPos = { x: data.bodies.sun.x, y: data.bodies.sun.y };
                } else {
                    DonateSky.sunScreenPos = null;
                }

                // Constellations drift — apply live alt/az → x/y projection
                // and parallactic-angle rotation to each .dn-constellation div.
                // Without this, constellations sit at their hard-coded blade
                // origins; with this, they slide across the sky over the night
                // (~15°/h sidereal rate) and rotate as they near the horizon.
                if (data.constellations) {
                    Object.keys(data.constellations).forEach(function (key) {
                        var c = data.constellations[key];
                        if (!c) return;
                        var el = document.querySelector('.dn-constellation[data-key="' + key + '"]');
                        if (!el) return;
                        if (!c.visible) {
                            el.classList.add('is-below-horizon');
                            return;
                        }
                        el.classList.remove('is-below-horizon');
                        el.style.left = c.x + '%';
                        el.style.top  = c.y + '%';
                        // Parallactic-angle rotation. Positive q means
                        // celestial north tilts clockwise from screen-up.
                        // We negate so the constellation visually rotates
                        // the right way as it rises and sets.
                        el.style.setProperty('--const-rotation', (-c.rotation).toFixed(1) + 'deg');
                    });
                }

                renderSkyLegend(visible, belowHorizon, data.eclipse, data.events);
            })
            .catch(function () { /* keep server-rendered defaults */ });
    }

    // Build the dark-side path of the Moon disc.
    //   R         — disc radius (SVG units, viewBox 100×100, so R=46)
    //   rxSigned  — terminator ellipse x-radius signed: -R..+R
    //                  -R = full moon (no dark side, path is empty)
    //                   0 = half moon (terminator is a vertical line)
    //                  +R = new moon  (entire disc dark)
    //   side      — +1 if dark side is on the LEFT, -1 if on the RIGHT
    //
    // The dark region = half-disc on the dark side + half-ellipse with the
    // signed radius; together they form a "lune" shape that always sits on
    // ONE half of the disc.
    function buildMoonTerminatorPath(R, rxSigned, side) {
        var cx = 50, cy = 50;
        // Top and bottom points of the disc (always shared with terminator)
        var top    = [cx, cy - R];
        var bottom = [cx, cy + R];
        // Outer half-circle along the dark side: sweep flag picks left vs right.
        // SVG arc: A rx ry x-axis-rot large-arc sweep x y
        // side=+1 (dark left): outer arc goes from TOP (counter-clockwise) to
        //   BOTTOM along the LEFT semicircle  → sweep=0
        // side=-1 (dark right): outer arc goes TOP→BOTTOM along RIGHT → sweep=1
        var outerSweep = side === 1 ? 0 : 1;
        // Inner ellipse half: same TOP→BOTTOM endpoints but along the
        // terminator. When rxSigned has the opposite sign of `side`, the
        // ellipse bulges INTO the lit side (waxing/waning crescent).
        // When rxSigned has the same sign as `side`, the ellipse bulges INTO
        // the dark side (gibbous — most of the disc is lit).
        var innerRx = Math.abs(rxSigned);
        // Inner sweep: opposite of outer to close the path correctly along
        // the inner curve.
        var innerSweep = (rxSigned * side >= 0) ? outerSweep : (1 - outerSweep);
        return 'M ' + top[0] + ' ' + top[1] +
               ' A ' + R + ' ' + R + ' 0 0 ' + outerSweep + ' ' + bottom[0] + ' ' + bottom[1] +
               ' A ' + innerRx + ' ' + R + ' 0 0 ' + innerSweep + ' ' + top[0] + ' ' + top[1] +
               ' Z';
    }

    // Apparent size mapping. Two regimes:
    //   • luminaries (Sun, Moon) — real angular size matters here because
    //     they're the only bodies that show as a disc to the naked eye.
    //     Use sqrt scaling so the user perceives their similar disk size.
    //   • everything else — pure log scaling on arcseconds, clamped 22..38 px.
    //     Detail SVGs (Saturn rings, Jupiter bands) need this — at 6 px
    //     they'd be unreadable, even though the real planet is a point of
    //     light at that distance.
    //
    // On narrow viewports we scale everything down so the sky doesn't
    // become "marbles in a small frame".
    function sizeForBody(name, arcsec) {
        var w = window.innerWidth || 1100;
        var responsiveScale = w < 380 ? 0.65 : (w < 560 ? 0.78 : (w < 780 ? 0.88 : 1));
        if (name === 'sun' || name === 'moon') {
            var px = 1.28 * Math.sqrt(Math.max(arcsec, 100));
            return Math.round(Math.max(40, Math.min(62, px)) * responsiveScale);
        }
        if (arcsec <= 0) return Math.round(24 * responsiveScale);
        var v = 18 + 8 * Math.log10(arcsec);
        return Math.round(Math.max(22, Math.min(38, v)) * responsiveScale);
    }

    function renderSkyLegend(visible, below, eclipse, events) {
        var el = document.getElementById('dn-sky-legend');
        if (!el) return;
        function nameList(arr) {
            return arr.map(function (n) { return BODY_NAMES[n] || n; }).join(' · ');
        }
        // Observer location: the place word + N/E abbreviations are localized
        // via I18; the coordinates themselves stay numeric.
        var place   = I18.default_place || 'Greenwich';
        var latAbbr = I18.lat_label || 'N';
        var lonAbbr = I18.lon_label || 'E';
        var observerLabel = '<span class="dn-legend-row dn-legend-observer">' +
                                '<span class="dn-legend-icon" aria-hidden="true">📍</span>' +
                                '<span class="dn-legend-label">' + (I18.observer || 'Observer:') + '</span>' +
                                '<span class="dn-legend-bodies">' + place +
                                    ' · 55.75°&nbsp;' + latAbbr + ', 37.62°&nbsp;' + lonAbbr + '</span>' +
                            '</span>';
        function fmtDate(iso) {
            try {
                var d = new Date(iso);
                // Format the date in the visitor's locale; fall back to the
                // browser default if __LOCALE isn't set.
                var loc = (window.__LOCALE ? (window.__LOCALE + '') : undefined);
                return d.toLocaleDateString(loc, { day: 'numeric', month: 'long' });
            } catch (_) { return ''; }
        }
        function dayWord(n) {
            // Plural selector kept generic. RU supplies three distinct forms via
            // day_one/day_few/day_many; languages with a single plural repeat
            // the same value across all three keys.
            n = Math.abs(n) % 100;
            var n1 = n % 10;
            var few  = I18.day_few  || 'days';
            var one  = I18.day_one  || 'day';
            var many = I18.day_many || 'days';
            if (n > 10 && n < 20) return many;
            if (n1 > 1 && n1 < 5)  return few;
            if (n1 === 1)          return one;
            return many;
        }

        var html = '';
        if (eclipse && eclipse.type) {
            var label = eclipse.type === 'solar'
                ? (I18.eclipse_solar || 'Solar eclipse')
                : (I18.eclipse_lunar || 'Lunar eclipse');
            var pct = Math.round((eclipse.magnitude || 0) * 100);
            html += '<span class="dn-legend-row dn-legend-eclipse">' +
                        '<span class="dn-legend-icon" aria-hidden="true">🌑</span>' +
                        '<span class="dn-legend-label">' + (I18.today || 'Today:') + '</span>' +
                        '<span class="dn-legend-bodies">' + label + ' · ' +
                            (I18.phase || 'magnitude') + ' ' + pct + '%</span>' +
                    '</span>';
        }
        if (visible.length) {
            html += '<span class="dn-legend-row dn-legend-up">' +
                        '<span class="dn-legend-icon" aria-hidden="true">🌌</span>' +
                        '<span class="dn-legend-label">' + (I18.in_sky_now || 'In the sky now:') + '</span>' +
                        '<span class="dn-legend-bodies">' + nameList(visible) + '</span>' +
                    '</span>';
        }
        if (below.length) {
            html += '<span class="dn-legend-row dn-legend-down">' +
                        '<span class="dn-legend-icon" aria-hidden="true">🌑</span>' +
                        '<span class="dn-legend-label">' + (I18.below_horizon || 'Below the horizon:') + '</span>' +
                        '<span class="dn-legend-bodies">' + nameList(below) + '</span>' +
                    '</span>';
        }
        // Upcoming countdowns (top 1–2). Gives users a reason to come back.
        if (events && events.length) {
            // Take only the 1–2 most imminent events. Show as "Up next: in 56
            // days" form, with a date hint in muted colour.
            for (var i = 0; i < Math.min(2, events.length); i++) {
                var ev = events[i];
                var dl = ev.days_left;
                var when;
                if (dl === 0) when = '<strong>' + (I18.today_excl || 'today!') + '</strong>';
                else if (dl === 1) when = (I18.tomorrow || 'tomorrow');
                else when = (I18.in_prefix || 'in') + ' ' + dl + ' ' + dayWord(dl);
                html += '<span class="dn-legend-row dn-legend-countdown">' +
                            '<span class="dn-legend-icon" aria-hidden="true">📅</span>' +
                            '<span class="dn-legend-label">' + (I18.until_event || 'Up next:') + '</span>' +
                            '<span class="dn-legend-bodies">' + ev.label + ' · ' + when +
                                '<span class="dn-legend-countdown-date">' + fmtDate(ev.date) + '</span>' +
                            '</span>' +
                        '</span>';
            }
        }
        // Observer label appended LAST — it's a fixed disclaimer, not a
        // changing event, so it sits at the bottom of the legend.
        if (html) html += observerLabel;
        el.innerHTML = html;
        el.hidden = !html;
    }

    DonateSky.reload = function () {
        var mode = (skyEl && skyEl.dataset.mode) || 'donate';
        // In 'cosmos' mode we DON'T fetch wishes — the constellation slots
        // are pre-lit decoratively (decorateConstellations below) and the
        // page is purely a sky viewer.
        var work = mode === 'cosmos'
            ? Promise.all([decorateConstellations(), loadSkyState()])
            : Promise.all([loadWishes(),            loadSkyState()]);
        return work.then(function () {
            startBrightRotation();
            startComets();
            startMeteors();
            startAurora();
            startSatellite();
            startSupernova();
            startSkyRefresh();
            // Cosmos mode: Polaris always visible (no "first paid wish" gate).
            if (mode === 'cosmos') togglePolaris(true);
        });
    };

    // Cosmos-mode constellation decorator: light up every slot with a
    // deterministic spectral colour and a soft pulse, so the sky is full
    // of stars without any wish data. Each slot uses its `data-color`
    // (set by the constellations config) when present, else a per-id
    // spectral pick.
    function decorateConstellations() {
        var slots = Array.prototype.slice.call(document.querySelectorAll('.dn-c-slot'));
        var rhythms = ['3.5s', '5s', '6.5s', '8s'];
        slots.forEach(function (slot, i) {
            var color = slot.dataset.color || pickType(i).pri;
            slot.classList.add('is-filled');
            slot.style.setProperty('--star-color', color);
            slot.style.setProperty('--pulse-dur',   rhythms[i % rhythms.length]);
            slot.style.setProperty('--pulse-delay', ((i % 30) / 10) + 's');
        });
        return Promise.resolve();
    }

    // Periodic sky refresh — Sun's azimuth drifts ~15°/h, so for a long-open
    // tab the planet positions go stale within an hour. Re-fetch every 5 min
    // (the API itself caches for 60 s, so this is cheap), but ONLY while the
    // tab is visible — visibilitychange pauses/resumes. On wake-up after a
    // long sleep, do an immediate refresh so the user sees the current sky.
    var skyRefreshTimer = null;
    var SKY_REFRESH_MS = 5 * 60 * 1000;
    function startSkyRefresh() {
        if (skyRefreshTimer) return;
        skyRefreshTimer = setInterval(function () {
            if (document.hidden) return;
            loadSkyState();
        }, SKY_REFRESH_MS);
        // Catch-up refresh when the tab comes back into focus after being
        // hidden for a while (mobile browsers throttle setInterval heavily
        // when backgrounded, so the timer alone isn't enough).
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) loadSkyState();
        });
    }

    // ───── Can this engine draw the moving decorations at all? ─────
    // Comets, meteors, satellites and the supernova move by the Web Animations
    // API. WebKit before iOS 13.4 has no `Element.animate` (Sentry
    // TIMBRICA-FRONTEND-P7/P8, 08.10: iPad iOS 12.5, /night-sky — every comet
    // and meteor threw «c.animate is not a function»). Meteors and the
    // supernova also animate the INDIVIDUAL `translate`/`scale` properties:
    // without them (Safari < 14.1, Chromium < 104) the shape would blink in
    // place instead of crossing the sky. An engine that cannot draw a
    // decoration simply has none; the sky itself stays.
    var CAN_ANIMATE = typeof Element !== 'undefined' && !!Element.prototype && typeof Element.prototype.animate === 'function';
    var CAN_MOVE_PARTS = CAN_ANIMATE && !!(window.CSS && typeof CSS.supports === 'function'
        && CSS.supports('translate', '1px 1px') && CSS.supports('scale', '1'));

    // ───── Comets — randomized via sky-comets.js (lazy) ─────
    function startComets() {
        if (!skyEl || !CAN_ANIMATE) return;
        // Skip on Low quality or reduce-motion (when low quality respects it)
        if (skyEl.classList.contains('dn-q-low')) return;
        DonateSky.loadModule('sky-comets').then(function () {
            if (window.DonateSkyComets) window.DonateSkyComets.start(skyEl);
        }).catch(function () {});
    }

    // ───── Meteors — fast streaks via sky-meteors.js (lazy) ─────
    // Cheap to run (one DOM node every 30–60 s), so we let them play even
    // on Low quality but not under prefers-reduced-motion (CSS handles that
    // via `.dn-sky.dn-q-low + reduce-motion → display:none`).
    function startMeteors() {
        if (!skyEl || !CAN_MOVE_PARTS) return;
        DonateSky.loadModule('sky-meteors').then(function () {
            if (window.DonateSkyMeteors) window.DonateSkyMeteors.start(skyEl);
        }).catch(function () {});
    }

    // ───── Aurora — rare green/pink curtains via sky-aurora.js (lazy) ─────
    function startAurora() {
        if (!skyEl) return;
        // Skip on Low quality — gradient layers + filter:blur are expensive
        // (CSS already hides the element on .dn-q-low).
        if (skyEl.classList.contains('dn-q-low')) return;
        DonateSky.loadModule('sky-aurora').then(function () {
            if (window.DonateSkyAurora) window.DonateSkyAurora.start();
        }).catch(function () {});
    }

    // ───── Satellite passes (ISS-like) — sky-satellite.js (lazy) ─────
    function startSatellite() {
        if (!skyEl || !CAN_ANIMATE) return;
        if (skyEl.classList.contains('dn-q-low')) return;
        DonateSky.loadModule('sky-satellite').then(function () {
            if (window.DonateSkySatellite) window.DonateSkySatellite.start(skyEl);
        }).catch(function () {});
    }

    // ───── Supernova — rare stellar explosion via sky-supernova.js (lazy) ─────
    function startSupernova() {
        if (!skyEl || !CAN_MOVE_PARTS) return;
        if (skyEl.classList.contains('dn-q-low')) return;
        DonateSky.loadModule('sky-supernova').then(function () {
            if (window.DonateSkySupernova) window.DonateSkySupernova.start(skyEl);
        }).catch(function () {});
    }

    // (Space Shuttle launch unhooked — Farid 2026-04-26: didn't like the
    // visual. The sky-shuttle.js module + CSS block are still on disk
    // (not lazy-loaded since startShuttle() isn't called from reload).
    // To re-enable: add `startShuttle();` back into DonateSky.reload's
    // chain below, plus a startShuttle() function definition that calls
    // DonateSky.loadModule('sky-shuttle').then(...).start({mode:'daily'}).
    // Or temporarily from the browser console:
    //   DonateSky.loadModule('sky-shuttle').then(()=>DonateSkyShuttle.start({mode:'test'})))

    // ───── Glow rotation ─────
    // Only N stars carry the full halo+spike treatment at once — others are
    // faint pinpoints. Every 8s we pick a different N. With this, even 1000
    // stars stay smooth: only N have heavy filters/box-shadows live.
    var BRIGHT_COUNT = 12;
    function eligibleStars() {
        return Array.prototype.slice.call(document.querySelectorAll('.dn-star, .dn-c-slot.is-filled'));
    }
    function rotateBrightStars() {
        var all = eligibleStars();
        if (!all.length) return;
        // Clear current bright marks
        all.forEach(function (s) { s.classList.remove('is-bright'); });
        // Shuffle (Fisher-Yates) and pick first BRIGHT_COUNT
        var pool = all.slice();
        for (var i = pool.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var t = pool[i]; pool[i] = pool[j]; pool[j] = t;
        }
        var n = Math.min(BRIGHT_COUNT, pool.length);
        for (var k = 0; k < n; k++) pool[k].classList.add('is-bright');
    }
    function startBrightRotation() {
        if (brightRotateTimer) return;
        rotateBrightStars();
        brightRotateTimer = setInterval(rotateBrightStars, 8000);
    }

    // ───── Mouse parallax — six depth layers + per-planet depth ─────
    // Background layers (nebulas/galaxy/constellations/mid stars/wishes)
    // share their layer-level transforms; PLANETS each get individual
    // transforms based on their distance from Earth — the Moon (0.0026 AU)
    // shifts a lot, Neptune (30 AU) barely moves. Strength comes from the
    // `data-parallax` attribute set in cosmic-sky.blade.php.
    function setupParallax() {
        if (!skyEl || !window.matchMedia) return;
        if (window.matchMedia('(pointer: coarse)').matches) return;  // no mouse on touch
        var rect = null;
        var planetEls = Array.prototype.slice.call(skyEl.querySelectorAll('.dn-planet'));
        planetEls.forEach(function (p) {
            p._parallax = parseFloat(p.dataset.parallax) || 1;
        });
        // Galaxy depth layers — each gets a different mouse-parallax
        // coefficient on top of its own time-based drift animation. Back
        // moves the LEAST (deeper / farther stars), front moves the MOST.
        var galaxyBack  = skyEl.querySelector('.dn-galaxy-back');
        var galaxyMid2  = skyEl.querySelector('.dn-galaxy-mid');
        var galaxyFront = skyEl.querySelector('.dn-galaxy-front');
        var BASE_PLANET_DX = -20;
        var BASE_PLANET_DY = -14;

        // We use `translate` (separate CSS property) for the parallax shift
        // so it composes cleanly with the existing CSS keyframe animation
        // running on `transform` for time-based drift.
        function setLayerXY(el, x, y) {
            if (el) el.style.translate = x.toFixed(1) + 'px ' + y.toFixed(1) + 'px';
        }

        skyEl.addEventListener('mouseenter', function () { rect = skyEl.getBoundingClientRect(); });
        skyEl.addEventListener('mousemove', function (e) {
            if (!rect) rect = skyEl.getBoundingClientRect();
            var rx = (e.clientX - rect.left) / rect.width  - 0.5;
            var ry = (e.clientY - rect.top)  / rect.height - 0.5;
            // Background layers
            if (farLayer)    farLayer.style.transform    = 'translate(' + (rx * -4)  + 'px,' + (ry * -3)  + 'px)';
            // Galaxy: per-depth parallax (back: -3, mid: -8, front: -14).
            // back/mid/front layers get `translate` so their existing
            // transform: animation isn't clobbered.
            setLayerXY(galaxyBack,  rx * -3,  ry * -2);
            setLayerXY(galaxyMid2,  rx * -8,  ry * -5);
            setLayerXY(galaxyFront, rx * -14, ry * -9);
            if (constLayer)  constLayer.style.transform  = 'translate(' + (rx * -12) + 'px,' + (ry * -8)  + 'px)';
            if (midLayer)    midLayer.style.transform    = 'translate(' + (rx * -16) + 'px,' + (ry * -11) + 'px)';
            if (starsLayer)  starsLayer.style.transform  = 'translate(' + (rx * -20) + 'px,' + (ry * -14) + 'px)';
            for (var i = 0; i < planetEls.length; i++) {
                var p = planetEls[i];
                var s = p._parallax;
                p.style.translate = (rx * BASE_PLANET_DX * s).toFixed(1) + 'px ' +
                                    (ry * BASE_PLANET_DY * s).toFixed(1) + 'px';
            }
        });
        skyEl.addEventListener('mouseleave', function () {
            [farLayer, constLayer, midLayer, starsLayer].forEach(function (l) {
                if (l) l.style.transform = 'translate(0,0)';
            });
            [galaxyBack, galaxyMid2, galaxyFront].forEach(function (l) {
                if (l) l.style.translate = '0 0';
            });
            planetEls.forEach(function (p) { p.style.translate = '0 0'; });
        });
    }

    // ───── Tap interactions for planets and constellation stagger ─────
    function setupInteractions() {
        document.querySelectorAll('.dn-planet').forEach(function (p) {
            p.addEventListener('click', function (e) {
                e.stopPropagation();
                document.querySelectorAll('.dn-planet.active').forEach(function (x) { if (x !== p) x.classList.remove('active'); });
                p.classList.toggle('active');
                // Click planet → lazy-load zoom + smoothly zoom-focus on it
                DonateSky.loadModule('sky-zoom').then(function () {
                    if (window.DonateSkyZoom) {
                        window.DonateSkyZoom.init(skyEl);
                        window.DonateSkyZoom.focusOn(p, 2.5);
                    }
                }).catch(function () { /* silent */ });
            });
        });

        // First wheel inside sky → load zoom module (preempt before user expects)
        var wheelHooked = false;
        if (skyEl) {
            skyEl.addEventListener('wheel', function () {
                if (wheelHooked) return;
                wheelHooked = true;
                DonateSky.loadModule('sky-zoom').then(function () {
                    if (window.DonateSkyZoom) window.DonateSkyZoom.init(skyEl);
                });
            }, { once: true, passive: true });
        }

        document.querySelectorAll('.dn-constellation').forEach(function (c) {
            c.querySelectorAll('.dn-constellation-stars circle').forEach(function (s, i) {
                s.style.setProperty('--star-delay', (i * 0.4) + 's');
            });
        });
        document.addEventListener('click', function (e) {
            if (tipEl && !e.target.closest('.dn-c-slot.is-filled') && !e.target.closest('.dn-star')) {
                tipEl.classList.remove('show');
            }
            if (!e.target.closest('.dn-star')) {
                document.querySelectorAll('.dn-star.active').forEach(function (s) { s.classList.remove('active'); });
            }
        });
    }

    // ───── Public: notify-focus hook used by sky-zoom ─────
    // el = focused element, or null (when reset/no focus → stop effects)
    DonateSky.notifyFocus = function (el) {
        if (!el) {
            if (window.DonateSkyFlares) window.DonateSkyFlares.stop();
            if (window.DonateSkyDetail) window.DonateSkyDetail.revertAll();
            return;
        }
        // GTA-3-style streaming: when the camera approaches a planet,
        // lazy-load the high-detail SVG and swap it in. Reverts on reset.
        if (el.classList && el.classList.contains('dn-planet')) {
            DonateSky.loadModule('sky-detail').then(function () {
                if (window.DonateSkyDetail) {
                    window.DonateSkyDetail.revertAll();
                    window.DonateSkyDetail.applyTo(el);
                }
            }).catch(function () {});
        }
        if (el.classList && el.classList.contains('dn-planet-sun')) {
            DonateSky.loadModule('sky-flares').then(function () {
                if (window.DonateSkyFlares) window.DonateSkyFlares.start(el);
            }).catch(function () {});
        } else if (window.DonateSkyFlares) {
            window.DonateSkyFlares.stop();
        }
    };

    // ───── Quality control widget ─────
    function setupQualityControl(initial) {
        if (!skyEl) return;
        var ctrl = document.createElement('div');
        ctrl.className = 'dn-sky-quality';
        var q = I18.quality || {};
        ctrl.innerHTML =
            '<label>' + (q.label || 'Quality') + ' ' +
                '<select aria-label="' + (q.aria || 'Sky rendering quality') + '">' +
                    '<option value="auto">' + (q.auto || 'Auto') + '</option>' +
                    '<option value="low">' + (q.low || 'Low') + '</option>' +
                    '<option value="high">' + (q.high || 'High') + '</option>' +
                '</select>' +
            '</label>';
        var sel = ctrl.querySelector('select');
        sel.value = (initial === 'high' || initial === 'low') ? initial : 'auto';
        sel.addEventListener('change', function () {
            DonateSky.applyQuality(sel.value);
        });
        // Prefer the new combined supporters-meta strip (chips left + meta right).
        // Fall back to the legacy "after sky element" position if the donate
        // template is older (e.g. cached HTML during a deploy).
        var meta = document.getElementById('dn-supporters-meta');
        if (meta) {
            meta.appendChild(ctrl);
        } else {
            skyEl.parentNode.insertBefore(ctrl, skyEl.nextSibling);
        }
    }

    // ───── Public: submit wish (used by blade form) ─────
    // Pro-only now: the author's name is the Pro profile (set on the server),
    // so the client sends only the wish text.
    DonateSky.submitWish = function (msg) {
        return fetch('/api/wishes', {
            method: 'POST',
            credentials: 'same-origin',
            // CSRF now enforced on /api/wishes. session-bootstrap refreshes the
            // <meta> token past PageCache; the global fetch wrapper retries once
            // on 419 if the user submits before that lands.
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || '',
            },
            body: JSON.stringify({ message: msg }),
        }).then(function (r) {
            return r.json().then(function (data) { return { status: r.status, data: data }; });
        });
    };

    // ───── Init ─────
    DonateSky.init = function () {
        skyEl       = document.getElementById('dn-sky');
        skySection  = document.getElementById('dn-sky-section');
        skyEmpty    = document.getElementById('dn-sky-empty');
        totalEl     = document.getElementById('dn-sky-total');
        starsLayer  = document.getElementById('dn-stars-layer');
        if (!skyEl) return;

        anonText = skyEl.dataset.anonText || 'Anonymous';

        farLayer    = skyEl.querySelector('.dn-sky-far');
        // Galaxy is split into 3 depth layers (back/mid/front). The first
        // matched element is just for the legacy single-layer code path; the
        // per-depth parallax loop in setupParallax picks all three by class.
        galaxyLayer = skyEl.querySelector('.dn-sky-galaxy-layer');
        midLayer    = skyEl.querySelector('.dn-sky-mid');
        constLayer  = skyEl.querySelector('.dn-sky-constellations');
        planetLayer = skyEl.querySelector('.dn-sky-planets');

        // Quality — default Auto. User can switch to Low or High via dropdown,
        // and that explicit choice is persisted (so Auto re-runs the heuristic
        // every fresh visit, but Low/High are sticky once chosen).
        var saved = null;
        try { saved = localStorage.getItem('dn_sky_q'); } catch (_) {}
        var qInit = (saved === 'high' || saved === 'low' || saved === 'auto') ? saved : 'auto';
        // Apply Low immediately as a safe baseline — full effects don't run
        // until the IO trigger calls applyQuality(qInit) below, which
        // re-evaluates Auto and may upgrade to High on capable devices.
        if (qInit === 'auto') {
            skyEl.classList.add('dn-q-low');
        } else {
            DonateSky.applyQuality(qInit);
        }
        setupQualityControl(qInit);

        setupParallax();
        setupInteractions();

        // Lazy: defer wish + sky-state load until section nears viewport.
        // We also resolve Auto → low|high here, because that's the moment we
        // actually need the quality decision (effects start mattering once the
        // sky paints). On devices that never scroll down, no work happens.
        var bootstrap = function () {
            if (loadedOnce) return;
            loadedOnce = true;
            if (qInit === 'auto') {
                // Re-resolve through applyQuality — single source of truth for
                // class management and persistence. Re-reads detectQuality().
                DonateSky.applyQuality('auto');
            }
            DonateSky.reload();
        };
        // Observe the section if there's one (donate page wraps the sky in
        // #dn-sky-section); otherwise observe the sky element itself —
        // the cosmic-sky tool may not have a wrapping section.
        var ioTarget = skySection || skyEl;
        if ('IntersectionObserver' in window && ioTarget) {
            var io = new IntersectionObserver(function (entries) {
                for (var i = 0; i < entries.length; i++) {
                    if (entries[i].isIntersecting) { bootstrap(); io.disconnect(); return; }
                }
            }, { rootMargin: '300px 0px' });
            io.observe(ioTarget);
        } else {
            bootstrap();
        }
    };

    window.DonateSky = DonateSky;
})(window, document);
